/**
 * Fu character designs — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_lunar_new_year`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const fuCharacterDesignsPack: ToilePack = {
    slug: 'fu-character-designs',
    category: 'season',
    name: 'Fu character designs',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'diamond', label: 'Red diamond', subject: 'the character "福" in black brush calligraphy on a red square turned on its point, a thin gold border inside the edge'},
        {key: 'upside', label: 'Upside down', subject: 'the character "福" printed upside down in gold on a red diamond, as it is hung on the door, a small tassel at the bottom corner'},
        {key: 'round', label: 'Round seal', subject: 'the character "福" in a round red seal, its strokes woven with plum blossoms and a small goat in the lower curve'},
    ],
    styles: [
        {key: 'brush', label: 'Brush calligraphy', style: 'bold ink brush calligraphy, thick tapering strokes, dry-brush edges', palette: 'red ground with black and gold strokes', framing: 'the design centered with an even margin'},
        {key: 'cutout', label: 'Paper cut-out', style: 'traditional paper cut window-flower style, solid shapes with cut-out openings, crisp edges', palette: 'red and gold on a plain ground', framing: 'the design centered with an even margin'},
        {key: 'icon', label: 'Flat icon', style: 'simple geometric flat icon, uniform strokes, rounded corners, hard clean edges, readable at small size', palette: 'red, gold and white, three flat colors', framing: 'the design centered and filling most of the square'},
    ],
    directions: ['with a fish', 'black only', 'small icon'],
    directionHint: 'try: with a fish',
    seo: {
        title: 'Fu Character Designs: 福 Good Fortune to Print',
        metaDescription: 'Fu character designs, the Chinese 福 for good fortune, to print or paste for the Spring Festival: brush, paper cut-out and flat icon.',
        h1: 'Fu character designs: 福 red diamond, upside down, round seal',
        tagline: 'Nine single Fu designs on a plain ground, three versions in three finishes: brush, paper cut-out, flat icon.',
        intro: 'The character 福, fu, means good fortune. It is pasted on doors and walls for the Spring Festival, often hung upside down, because the word for upside down sounds like the word for arrived. Each picture is one Fu design on a plain ground, to print, paste or use as an icon: a red diamond, an upside-down gold Fu, or a round red seal woven with plum blossom. Type a different version to change it.',
        useCases: [
            'A paper sign for a front door',
            'A wall decoration for the New Year',
            'A social media icon for the festival',
            'A print or card design',
        ],
        faq: [
            {q: 'Why is the character sometimes upside down?', a: 'Upside down, 倒 sounds like 到, arrived, so the fortune has arrived. One of the three designs shows it that way.'},
            {q: 'What does 福 mean?', a: 'Fu means good fortune or blessing.'},
            {q: 'What is the difference between the three styles?', a: 'Brush calligraphy has black and gold strokes on red, paper cut-out has solid shapes with openings, and flat icon uses three flat colours and reads at small sizes.'},
            {q: 'Can I change the design?', a: 'Yes. Type a different version, such as with a fish; the result may differ from the sample.'},
        ],
    },
}
